/**
 * MaintenanceWindowsTab — el platform admin publica los avisos de ventana de
 * mantenimiento que ven TODOS los tenants sobre el header.
 *
 * Las tres fechas confunden si no se explican, así que el formulario las nombra
 * por lo que hacen: cuándo empieza a avisarse, y cuándo empieza y termina la
 * intervención. Al pasar el fin, el aviso desaparece solo.
 */
import { useState } from 'react'
import { Plus, Trash2, Loader2, AlertTriangle, Wrench, Eye, EyeOff } from 'lucide-react'
import {
  useMaintenanceWindows, useCreateMaintenanceWindow,
  useUpdateMaintenanceWindow, useDeleteMaintenanceWindow,
} from '../hooks/usePlatform'
import type { MaintenanceWindowAdminDto, MaintenanceSeverity } from '../hooks/usePlatform'
import { Button } from '@/components/Button'
import { ToastContainer, useToast } from '@/components/Toast'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage } from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

const labelCls = 'block text-xs font-medium text-content-muted mb-1'

const SEVERIDADES: { value: MaintenanceSeverity; label: string; hint: string }[] = [
  { value: 'info', label: 'Informativo', hint: 'Azul. Se puede cerrar.' },
  { value: 'warning', label: 'Importante', hint: 'Ámbar. Se puede cerrar.' },
  { value: 'critical', label: 'Crítico', hint: 'Rojo. NO se puede cerrar.' },
]

const ESTADO_LABEL: Record<string, { txt: string; cls: string }> = {
  scheduled: { txt: 'Anunciada', cls: 'bg-primary-50 text-primary-hover' },
  imminent: { txt: 'Por comenzar', cls: 'bg-warning-surface-strong text-warning-content-strong' },
  in_progress: { txt: 'En curso', cls: 'bg-danger-surface-strong text-danger-content-strong' },
}

/** `datetime-local` necesita 'YYYY-MM-DDTHH:mm' en hora local. */
function paraInput(iso: string): string {
  const d = new Date(iso)
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 16)
}

function Preview({ title, message, severity }: { title: string; message: string; severity: MaintenanceSeverity }) {
  const colores: Record<MaintenanceSeverity, string> = {
    info: 'bg-primary-600 text-white',
    warning: 'bg-warning-solid text-amber-950',
    critical: 'bg-danger-fill text-white',
  }
  return (
    <div>
      <p className={labelCls}>Así lo verán los usuarios</p>
      <div className={`flex items-center justify-center gap-2.5 px-10 py-2 text-sm rounded-lg ${colores[severity]}`}>
        <AlertTriangle size={16} aria-hidden="true" />
        <p className="text-center leading-snug">
          <span className="font-semibold">{title || 'Título del aviso'}</span>
          <span className="hidden sm:inline"> — Mantenimiento programado </span>
          <span className="opacity-95">{message || 'Mensaje del aviso.'}</span>
        </p>
      </div>
    </div>
  )
}

export function MaintenanceWindowsTab() {
  const { data, isLoading } = useMaintenanceWindows()
  const crear = useCreateMaintenanceWindow()
  const actualizar = useUpdateMaintenanceWindow()
  const borrar = useDeleteMaintenanceWindow()
  const toast = useToast()

  const [form, setForm] = useState({
    title: 'Mantenimiento programado',
    message: '',
    severity: 'info' as MaintenanceSeverity,
    show_from: '',
    starts_at: '',
    ends_at: '',
    dismissible: true,
  })
  const [abierto, setAbierto] = useState(false)

  const ventanas = data?.windows ?? []

  const handleCrear = () => {
    if (!form.message.trim() || !form.show_from || !form.starts_at || !form.ends_at) {
      toast.error('Completa el mensaje y las tres fechas.')
      return
    }
    crear.mutate(
      {
        ...form,
        show_from: new Date(form.show_from).toISOString(),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      },
      {
        onSuccess: () => {
          toast.success('Aviso publicado. Ya lo ven todos los tenants.')
          setAbierto(false)
          setForm(f => ({ ...f, message: '', show_from: '', starts_at: '', ends_at: '' }))
        },
        onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo publicar el aviso.')),
      },
    )
  }

  const toggleActivo = (v: MaintenanceWindowAdminDto) => {
    actualizar.mutate(
      { id: v.id, data: { is_active: !v.is_active } },
      { onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo cambiar el estado.')) },
    )
  }

  const handleBorrar = (v: MaintenanceWindowAdminDto) => {
    borrar.mutate(v.id, {
      onSuccess: () => toast.success('Aviso eliminado.'),
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo eliminar.')),
    })
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-content">Ventanas de mantenimiento</h3>
          <p className="text-xs text-content-muted mt-0.5 max-w-2xl">
            El aviso aparece sobre el encabezado, en todas las pantallas y para todos los tenants.
            El usuario puede cerrarlo y vuelve a aparecer al día siguiente. Cuando falta menos de una
            hora, o mientras la ventana está en curso, no se puede cerrar. Al pasar el fin desaparece solo.
          </p>
        </div>
        <Button onClick={() => setAbierto(v => !v)} className="shrink-0">
          <Plus size={14} /> Nuevo aviso
        </Button>
      </div>

      {abierto && (
        <div className="p-4 rounded-card border border-divider bg-glass backdrop-blur-glass space-y-4 shadow-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              dense
              label="Título"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
            <SelectField
              dense
              label="Severidad"
              value={form.severity}
              onChange={e => setForm(f => ({ ...f, severity: e.target.value as MaintenanceSeverity }))}
            >
              {SEVERIDADES.map(s => <option key={s.value} value={s.value}>{s.label} — {s.hint}</option>)}
            </SelectField>
          </div>

          <TextField
            dense
            label="Mensaje"
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            placeholder="Ej: El sistema no estará disponible mientras aplicamos una actualización."
            inputProps={{ maxLength: 400 }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TextField
              dense
              label="Empezar a avisar"
              value={form.show_from}
              onChange={e => setForm(f => ({ ...f, show_from: e.target.value }))}
              inputProps={{ type: 'datetime-local' }}
              hint="Normalmente unos días antes."
            />
            <TextField
              dense
              label="Inicio del mantenimiento"
              value={form.starts_at}
              onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
              inputProps={{ type: 'datetime-local' }}
            />
            <TextField
              dense
              label="Fin del mantenimiento"
              value={form.ends_at}
              onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
              inputProps={{ type: 'datetime-local' }}
              hint="Al pasar, el aviso se retira solo."
            />
          </div>

          <Preview title={form.title} message={form.message} severity={form.severity} />

          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setAbierto(false)}>Cancelar</Button>
            <Button onClick={handleCrear} disabled={crear.isPending}>
              {crear.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Publicar aviso
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-subtle" /></div>
      ) : ventanas.length === 0 ? (
        <EmptyState title="No hay avisos de mantenimiento publicados." size="sm" />
      ) : (
        <div className="border border-divider rounded-xl divide-y divide-divider overflow-hidden">
          {ventanas.map(v => {
            const estado = v.state ? ESTADO_LABEL[v.state] : null
            return (
              <div key={v.id} className="flex items-center gap-3 px-4 py-3">
                <Wrench size={15} className="text-content-subtle shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-content truncate">{v.title}</p>
                    {estado && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${estado.cls}`}>
                        {estado.txt}
                      </span>
                    )}
                    {!v.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-content-subtle">
                        Desactivado
                      </span>
                    )}
                    <span className="text-[10px] text-content-subtle">{v.severity_label}</span>
                  </div>
                  <p className="text-xs text-content-muted truncate">{v.message}</p>
                  <p className="text-[11px] text-content-subtle mt-0.5">
                    {new Date(v.starts_at).toLocaleString('es-CL')} → {new Date(v.ends_at).toLocaleString('es-CL')}
                    {' · avisa desde '}{new Date(v.show_from).toLocaleString('es-CL')}
                  </p>
                </div>
                <button
                  onClick={() => toggleActivo(v)}
                  title={v.is_active ? 'Bajar el aviso sin borrarlo' : 'Volver a publicar'}
                  className="p-2 rounded-lg text-content-muted hover:bg-soft shrink-0"
                >
                  {v.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button
                  onClick={() => handleBorrar(v)}
                  title="Eliminar"
                  className="p-2 rounded-lg text-danger-content-soft hover:bg-danger-surface shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
